// Tiny event bus between the API client (src/api.ts) and the session logic
// (session-handler.ts). api.ts cannot import the auth store or the router
// (the store imports api.ts), so it only emits events here.

export type SessionEvent =
  | { type: "tokensRefreshed"; accessToken: string; refreshToken: string }
  | { type: "refreshFailed"; error: Error }
  // 401 that survived the SDK's single refresh-and-retry: the session is over.
  | { type: "unauthenticated" }
  // 403 `organization_frozen` on an authenticated call. On its own it does not
  // say whether the whole session or just one organization is suspended.
  | { type: "organizationFrozen"; endpoint: string }
  // 403 `organization_onboarding_incomplete`: the call needs a session that
  // has left self-service onboarding (card confirmed).
  | { type: "onboardingIncomplete" }
  // Emitted by the session handler (not the SDK): an action failed because
  // this one organization is suspended while the session is not. The app
  // shell says so; the failing screen may still show its own error.
  | { type: "orgSuspendedAction"; orgId: string }
  // Emitted by the auth store when the user switches organization.
  | { type: "organizationSelected"; organizationId: string };

type Listener = (event: SessionEvent) => void | Promise<void>;

const listeners = new Set<Listener>();

export const sessionEvents = {
  on(listener: Listener): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },

  // Resolves once every listener has finished, so the SDK can await a
  // listener (e.g. the store updating its token) before continuing.
  async emit(event: SessionEvent): Promise<void> {
    await Promise.all(
      [...listeners].map(async (listener) => {
        try {
          await listener(event);
        } catch (err) {
          console.error("Session event listener failed", err);
        }
      }),
    );
  },
};
